import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define available languages
export type Language = 'en' | 'ru' | 'uz';

// Create the context interface
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
}

// Create context with a default value
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Sample translations
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Header
    'home': 'Home',
    'products': 'Products',
    'services': 'Services',
    'branches': 'Branches',
    'about_us': 'About Us',
    'contact': 'Contact',
    'search': 'Search',
    'cart': 'Cart',
    'login': 'Login',
    'profile': 'Profile',
    
    // Buttons
    'explore_our_services': 'Explore Our Services',
    'contact_us': 'Contact Us',
    'continue_shopping': 'Continue Shopping',
    'clear_cart': 'Clear Cart',
    'proceed_to_checkout': 'Proceed to Checkout',
    
    // Product-related
    'ecu_control_units': 'ECU Control Units',
    'rail_injectors': 'Rail Injectors',
    'gas_reducers': 'Gas Reducers',
    'all_products': 'All Products',
    'product_page_description': 'Explore our selection of high-quality {category} designed for optimal performance and reliability.',
    'search_products_placeholder': 'Search products...',
    'sort_by': 'Sort By',
    'price_low_to_high': 'Price: Low to High',
    'price_high_to_low': 'Price: High to Low',
    'name_a_to_z': 'Name: A to Z',
    'name_z_to_a': 'Name: Z to A',
    'filters': 'Filters',
    'price_range': 'Price Range',
    'min_price': 'Minimum Price',
    'max_price': 'Maximum Price',
    'no_products_found': 'No products found',
    'try_different_search': 'Try adjusting your search or filter criteria',
    'updating_products': 'Updating products...',
    
    // Service page
    'our_services': 'Our Services',
    'service_description': 'EuropeGAS offers comprehensive solutions for gas equipment, from installation to maintenance and optimization.',
    'core_services': 'Core Services',
    'core_services_description': 'We provide a comprehensive range of specialized services to meet all your gas equipment and rail system needs.',
    'equipment_installation': 'Equipment Installation',
    'equipment_installation_desc': 'Professional installation of gas equipment and rail systems with precision and care.',
    'maintenance_repair': 'Maintenance & Repair',
    'maintenance_repair_desc': 'Comprehensive maintenance and repair services to keep your systems running optimally.',
    'consultation_services': 'Consultation Services',
    'consultation_services_desc': 'Expert advice and planning for your gas and rail system needs.',
    'system_optimization': 'System Optimization',
    'system_optimization_desc': 'Enhance performance and efficiency of your existing systems.',
    'additional_services': 'Additional Services',
    'performance_testing': 'Performance Testing',
    'performance_testing_desc': 'Comprehensive testing to ensure your systems meet the highest performance standards.',
    'delivery_logistics': 'Delivery & Logistics',
    'delivery_logistics_desc': 'Reliable delivery services for all equipment and parts throughout Uzbekistan.',
    'equipment_testing': 'Equipment Testing',
    'equipment_testing_desc': 'Thorough testing services to ensure all equipment meets safety and performance standards.',
    'training_programs': 'Training Programs',
    'training_programs_desc': 'Comprehensive training for operators and maintenance personnel on proper equipment usage and care.',
    'our_service_process': 'Our Service Process',
    'consultation': 'Consultation',
    'consultation_desc': 'We discuss your needs and requirements in detail',
    'assessment': 'Assessment',
    'assessment_desc': 'Our experts evaluate your specific situation',
    'implementation': 'Implementation',
    'implementation_desc': 'We perform the required service or installation',
    'follow_up': 'Follow-up',
    'follow_up_desc': 'We ensure your complete satisfaction',
    'ready_to_get_started': 'Ready to Get Started?',
    'cta_description': 'Contact us today to discuss how we can help with your gas equipment and rail solution needs.',
    
    // Service details
    'gas_conversion_systems': 'Gas conversion systems',
    'fuel_management_systems': 'Fuel management systems',
    'rail_equipment_setup': 'Rail equipment setup',
    'preventive_maintenance': 'Preventive maintenance',
    'emergency_repairs': 'Emergency repairs',
    'system_diagnostics': 'System diagnostics',
    'system_design': 'System design',
    'efficiency_analysis': 'Efficiency analysis',
    'regulatory_compliance': 'Regulatory compliance',
    'performance_tuning': 'Performance tuning',
    'efficiency_upgrades': 'Efficiency upgrades',
    'system_integration': 'System integration',
    
    // HomePage
    'home_hero_alt': 'Automotive Gas Equipment',
    'home_hero_title': 'Premium Automotive Gas Equipment',
    'home_hero_subtitle': 'Engineered for excellence. Designed for performance.',
    'explore_products': 'Explore Products',
    'learn_more': 'Learn More',
    'innovation_section_title': 'Innovation at its finest',
    'innovation_section_description': 'With over 15 years of experience, we\'ve perfected the art of automotive gas equipment, delivering unparalleled quality and performance.',
    'feature_1_title': 'Quality Guarantee',
    'feature_1_description': 'All our products come with a warranty and quality assurance for your peace of mind.',
    'feature_2_title': 'Expert Installation',
    'feature_2_description': 'Our certified technicians ensure proper installation for optimal performance.',
    'feature_3_title': '12 Regional Branches',
    'feature_3_description': 'With branches across Uzbekistan, we\'re always close to our customers.',
    'feature_4_title': '100,000+ Customers',
    'feature_4_description': 'Join our large family of satisfied customers who trust our products and services.',
    
    // Footer
    'company': 'Company',
    'quick_links': 'Quick Links',
    'newsletter': 'Newsletter',
    'copyright': 'Copyright © 2024 EuropeGAS. All rights reserved.',
    
    // Language
    'language': 'Language',
    'english': 'English',
    'uzbek': 'Uzbek',

    // About Page
    about_page_title: 'About Us',
    about_hero_alt: 'EuropeGAS Headquarters',
    about_hero_description: 'EuropeGAS — Pioneering automotive gas solutions since 2005.',
    our_mission: 'Our Mission',
    mission_description: 'We provide sustainable and efficient energy solutions that meet the evolving needs of our clients while maintaining the highest standards of quality, safety, and environmental responsibility.',
    innovation: 'Innovation',
    innovation_description: 'We continuously push the boundaries of what\'s possible in automotive gas technology, developing solutions that are more efficient, reliable, and user-friendly.',
    customer_focus: 'Customer Focus',
    customer_focus_description: 'Every product we design and service we provide is centered around our customers\' needs, ensuring they receive the best possible experience and value.',
    our_journey: 'Our Journey',
    journey_description: 'From our humble beginnings to becoming a leader in automotive gas solutions in Central Asia.',
    foundation: 'Foundation',
    foundation_description: 'EuropeGAS was established in Tashkent, Uzbekistan, with a vision to provide high-quality automotive gas solutions.',
    expansion: 'Expansion',
    expansion_description: 'We expanded our operations to multiple regions across Uzbekistan and established partnerships with European manufacturers.',
    today: 'Today',
    today_description: 'Now with 12 branches across the country and over 100,000 satisfied customers, we continue to lead the market with innovative solutions.',
    our_values: 'Our Values',
    values_description: 'The principles that guide everything we do.',
    integrity: 'Integrity',
    integrity_description: 'We conduct our business with the highest ethical standards, ensuring transparency and trust in all our relationships.',
    teamwork: 'Teamwork',
    teamwork_description: 'We believe in the power of collaboration, working together across departments and with our partners to achieve common goals.',
    excellence: 'Excellence',
    excellence_description: 'We strive for excellence in everything we do, from product design and manufacturing to customer service and support.',
    sustainability: 'Sustainability',
    sustainability_description: 'We are committed to environmentally responsible practices, developing solutions that reduce emissions and promote cleaner energy.',
    our_team: 'Our Team',
    team_description: 'Meet the team driving our vision forward.',
    ceo_name: 'Alisher Karimov',
    ceo_title: 'Chief Executive Officer',
    ceo_description: 'With over 20 years of experience in the automotive industry, Alisher leads our company with vision and expertise.',
    cto_name: 'Dilshod Rakhimov',
    cto_title: 'Chief Technology Officer',
    cto_description: 'Dilshod oversees our technical operations, ensuring we stay at the cutting edge of automotive gas technology.',
    coo_name: 'Nodira Azimova',
    coo_title: 'Chief Operations Officer',
    coo_description: 'Nodira ensures our day-to-day operations run smoothly, coordinating our 12 branches across Uzbekistan.',
    join_our_journey: 'Join Our Journey',
    join_journey_description: 'Whether you\'re a customer, partner, or potential team member, we\'d love to connect with you.',
    view_openings: 'View Openings',

    // Branches Page
    our_branches: 'Our Branches',
    find_branches_description: 'Find EuropeGAS branches and service centers across Uzbekistan',
    search_branches_placeholder: 'Search branches by name, city or address...',
    clear: 'Clear',
    region: 'Region',
    all_regions: 'All Regions',
    locations: 'Locations',
    branch: 'Branch',
    branches_label: 'Branches',
    no_branches_found: 'No branches found matching your criteria',
    clear_filters: 'Clear filters',
    interactive_map: 'Interactive Map',
    no_branches_for_map: 'No branches to display on map',
    loading_map: 'Loading map...',
    city: 'City',
    phone: 'Phone',
    working_hours: 'Working Hours',
    weekdays: 'Weekdays',
    saturday: 'Saturday',
    sunday: 'Sunday',
    address: 'Address',
    email: 'Email',
    
    // Contact Page
    'get_in_touch': 'Get In Touch',
    'send_us_message': 'Send Us a Message',
    'your_name': 'Your Name',
    'your_email': 'Your Email',
    'subject': 'Subject',
    'your_message': 'Your Message',
    'send_message': 'Send Message',
    'sending': 'Sending...',
    'message_sent': 'Message sent successfully!',
    'find_us_on_map': 'Find Us on the Map',
    
    // Cart Page
    'shopping_cart': 'Shopping Cart',
    'review_selected_products': 'Review your selected EuropeGAS products',
    'product': 'Product',
    'price': 'Price',
    'quantity': 'Quantity',
    'total': 'Total',
    'remove': 'Remove',
    'cart_empty': 'Your cart is empty',
    'start_shopping': 'Start shopping to add items to your cart',
    'order_summary': 'Order Summary',
    'subtotal': 'Subtotal',
    'shipping': 'Shipping',
    'free': 'Free',
    'total_amount': 'Total Amount',
    'checkout': 'Checkout',
    
    // Product Detail Page
    'product_details': 'Product Details',
    'add_to_cart': 'Add to Cart',
    'in_stock': 'In Stock',
    'out_of_stock': 'Out of Stock',
    'features': 'Features',
    'specifications': 'Specifications',
    'related_products': 'Related Products',
    'customer_reviews': 'Customer Reviews',
    'rating': 'Rating',
    'reviews': 'Reviews',
    'back_to_products': 'Back to Products',
    'availability': 'Availability',
    'key_features': 'Key Features',
    
    // Common UI
    'loading': 'Loading...',
    'error': 'Error',
    'try_again': 'Try Again',
    'close': 'Close',
    'open': 'Open',
    'save': 'Save',
    'cancel': 'Cancel',
    'confirm': 'Confirm',
    'yes': 'Yes',
    'no': 'No',
    'next': 'Next',
    'previous': 'Previous',
    'view_all': 'View All',
    'show_more': 'Show More',
    'show_less': 'Show Less',
    
    // HomePage specific
    'featured_products': 'Featured Products',
    'customer_testimonials': 'What Our Customers Say',
    'find_us_nearby': 'Find Us Nearby',
    'view_details': 'View Details',
    'product_categories': 'Product Categories',
    'featured_products_description': 'Discover our most popular automotive gas equipment, designed with precision and built to last.',
    'view_all_products': 'View All Products',
    'customer_testimonials_description': 'Don\'t just take our word for it. Hear from our satisfied customers across Uzbekistan.',
    'find_us_nearby_description': '150 branches across Uzbekistan - We\'re never far away. Visit us today.',
    'found': 'found',
    'show_all_cities': 'Show All Cities',
    'ready_to_upgrade': 'Ready to upgrade your vehicle?',
    'upgrade_description': 'Experience the difference with EuropeGAS\'s premium automotive gas equipment.',
    'contact_us_today': 'Contact Us Today',
    
    // Missing translation keys
    'company_description': 'Uzbekistan',
    'empowering_mission': 'Empowering Uzbekistan\'s automotive future with innovative gas solutions and uncompromising quality',
    'contact_description': 'We\'d love to hear from you. Get in touch with our team.',
    'contact_form_description': 'Have a question or want to work together? We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.',
    'enter_full_name': 'Enter your full name',
    'enter_email_address': 'Enter your email address',
    'message_subject_placeholder': 'What is this message about?',
    'message_placeholder': 'Tell us more about your inquiry...',
    'monday_friday': 'Monday - Friday:',
    'sunday_closed': 'Sunday: Closed',
    'headquarters_description': 'Visit our headquarters in the heart of Tashkent. We\'re easily accessible and always ready to welcome you.',
    'our_location': 'Our Location',
    'phone_numbers': 'Phone Numbers',
    'email_address': 'Email Address',
    'privacy_policy': 'Privacy Policy',
    'terms_of_service': 'Terms of Service',
  },
  ru: {
    // Header
    'home': 'Главная',
    'products': 'Продукты',
    'services': 'Услуги',
    'branches': 'Филиалы',
    'about_us': 'О нас',
    'contact': 'Контакты',
    'search': 'Поиск',
    'cart': 'Корзина',
    'login': 'Войти',
    'profile': 'Профиль',
    
    // Buttons
    'explore_our_services': 'Изучить наши услуги',
    'contact_us': 'Связаться с нами',
    'continue_shopping': 'Продолжить покупки',
    'clear_cart': 'Очистить корзину',
    'proceed_to_checkout': 'Перейти к оформлению',
    
    // Product-related
    'ecu_control_units': 'Блоки управления ECU',
    'rail_injectors': 'Рейловые инжекторы',
    'gas_reducers': 'Газовые редукторы',
    'all_products': 'Все продукты',
    'product_page_description': 'Изучите наш выбор высококачественных {category}, разработанных для оптимальной производительности и надежности.',
    'search_products_placeholder': 'Поиск продуктов...',
    'sort_by': 'Сортировать по',
    'price_low_to_high': 'Цена: по возрастанию',
    'price_high_to_low': 'Цена: по убыванию',
    'name_a_to_z': 'Название: А-Я',
    'name_z_to_a': 'Название: Я-А',
    'filters': 'Фильтры',
    'price_range': 'Диапазон цен',
    'min_price': 'Минимальная цена',
    'max_price': 'Максимальная цена',
    'no_products_found': 'Продукты не найдены',
    'try_different_search': 'Попробуйте изменить критерии поиска или фильтра',
    'updating_products': 'Обновление продуктов...',
    
    // Service page
    'our_services': 'Наши услуги',
    'service_description': 'EuropeGAS предлагает комплексные решения для газового оборудования, от установки до обслуживания и оптимизации.',
    'core_services': 'Основные услуги',
    'core_services_description': 'Мы предоставляем широкий спектр специализированных услуг для удовлетворения всех ваших потребностей в газовом оборудовании и железнодорожных системах.',
    'equipment_installation': 'Установка оборудования',
    'equipment_installation_desc': 'Профессиональная установка газового оборудования и железнодорожных систем с точностью и заботой.',
    'maintenance_repair': 'Обслуживание и ремонт',
    'maintenance_repair_desc': 'Комплексные услуги по обслуживанию и ремонту для поддержания оптимальной работы ваших систем.',
    'consultation_services': 'Консультационные услуги',
    'consultation_services_desc': 'Экспертные консультации и планирование для ваших потребностей в газовых и железнодорожных системах.',
    'system_optimization': 'Оптимизация системы',
    'system_optimization_desc': 'Повышение производительности и эффективности ваших существующих систем.',
    'additional_services': 'Дополнительные услуги',
    'performance_testing': 'Тестирование производительности',
    'performance_testing_desc': 'Комплексное тестирование для обеспечения соответствия ваших систем самым высоким стандартам производительности.',
    'delivery_logistics': 'Доставка и логистика',
    'delivery_logistics_desc': 'Надежные услуги доставки всего оборудования и запчастей по всему Узбекистану.',
    'equipment_testing': 'Тестирование оборудования',
    'equipment_testing_desc': 'Тщательные услуги тестирования для обеспечения соответствия всего оборудования стандартам безопасности и производительности.',
    'training_programs': 'Программы обучения',
    'training_programs_desc': 'Комплексное обучение операторов и обслуживающего персонала правильному использованию и уходу за оборудованием.',
    'our_service_process': 'Наш процесс обслуживания',
    'consultation': 'Консультация',
    'consultation_desc': 'Мы подробно обсуждаем ваши потребности и требования',
    'assessment': 'Оценка',
    'assessment_desc': 'Наши эксперты оценивают вашу конкретную ситуацию',
    'implementation': 'Реализация',
    'implementation_desc': 'Мы выполняем необходимые услуги или установку',
    'follow_up': 'Последующее наблюдение',
    'follow_up_desc': 'Мы обеспечиваем ваше полное удовлетворение',
    'ready_to_get_started': 'Готовы начать?',
    'cta_description': 'Свяжитесь с нами сегодня, чтобы обсудить, как мы можем помочь с вашими потребностями в газовом оборудовании и железнодорожных решениях.',
    
    // Service details
    'gas_conversion_systems': 'Системы газового преобразования',
    'fuel_management_systems': 'Системы управления топливом',
    'rail_equipment_setup': 'Настройка железнодорожного оборудования',
    'preventive_maintenance': 'Профилактическое обслуживание',
    'emergency_repairs': 'Аварийный ремонт',
    'system_diagnostics': 'Диагностика системы',
    'system_design': 'Проектирование системы',
    'efficiency_analysis': 'Анализ эффективности',
    'regulatory_compliance': 'Соответствие нормативам',
    'performance_tuning': 'Настройка производительности',
    'efficiency_upgrades': 'Модернизация эффективности',
    'system_integration': 'Интеграция системы',
    
    // HomePage
    'home_hero_alt': 'Автомобильное газовое оборудование',
    'home_hero_title': 'Премиальное автомобильное газовое оборудование',
    'home_hero_subtitle': 'Создано для совершенства. Разработано для производительности.',
    'explore_products': 'Изучить продукты',
    'learn_more': 'Узнать больше',
    'innovation_section_title': 'Инновации в лучшем виде',
    'innovation_section_description': 'С более чем 15-летним опытом мы усовершенствовали искусство автомобильного газового оборудования, обеспечивая непревзойденное качество и производительность.',
    'feature_1_title': 'Гарантия качества',
    'feature_1_description': 'Все наши продукты поставляются с гарантией и обеспечением качества для вашего спокойствия.',
    'feature_2_title': 'Экспертная установка',
    'feature_2_description': 'Наши сертифицированные техники обеспечивают правильную установку для оптимальной производительности.',
    'feature_3_title': '12 региональных филиалов',
    'feature_3_description': 'С филиалами по всему Узбекистану мы всегда рядом с нашими клиентами.',
    'feature_4_title': '100,000+ клиентов',
    'feature_4_description': 'Присоединяйтесь к нашей большой семье довольных клиентов, которые доверяют нашим продуктам и услугам.',
    
    // Footer
    'company': 'Компания',
    'quick_links': 'Быстрые ссылки',
    'newsletter': 'Новости',
    'copyright': 'Авторские права © 2024 EuropeGAS. Все права защищены.',
    
    // Language
    'language': 'Язык',
    'english': 'Английский',
    'russian': 'Русский',
    'uzbek': 'Узбекский',

    // About Page
    about_page_title: 'О нас',
    about_hero_alt: 'Штаб-квартира EuropeGAS',
    about_hero_description: 'EuropeGAS — Пионеры автомобильных газовых решений с 2005 года.',
    our_mission: 'Наша миссия',
    mission_description: 'Мы предоставляем устойчивые и эффективные энергетические решения, которые отвечают развивающимся потребностям наших клиентов, поддерживая при этом самые высокие стандарты качества, безопасности и экологической ответственности.',
    innovation: 'Инновации',
    innovation_description: 'Мы постоянно расширяем границы возможного в технологии автомобильного газа, разрабатывая решения, которые более эффективны, надежны и удобны для пользователя.',
    customer_focus: 'Ориентация на клиента',
    customer_focus_description: 'Каждый продукт, который мы разрабатываем, и каждая услуга, которую мы предоставляем, сосредоточены на потребностях наших клиентов, обеспечивая им лучший возможный опыт и ценность.',
    our_journey: 'Наш путь',
    journey_description: 'От скромного начала до становления лидером в автомобильных газовых решениях в Центральной Азии.',
    foundation: 'Основание',
    foundation_description: 'EuropeGAS была основана в Ташкенте, Узбекистан, с видением предоставления высококачественных автомобильных газовых решений.',
    expansion: 'Расширение',
    expansion_description: 'Мы расширили наши операции на несколько регионов Узбекистана и установили партнерские отношения с европейскими производителями.',
    today: 'Сегодня',
    today_description: 'Теперь с 12 филиалами по всей стране и более чем 100,000 довольных клиентов, мы продолжаем лидировать на рынке с инновационными решениями.',
    our_values: 'Наши ценности',
    values_description: 'Принципы, которые направляют все, что мы делаем.',
    integrity: 'Честность',
    integrity_description: 'Мы ведем наш бизнес с самыми высокими этическими стандартами, обеспечивая прозрачность и доверие во всех наших отношениях.',
    teamwork: 'Командная работа',
    teamwork_description: 'Мы верим в силу сотрудничества, работая вместе между отделами и с нашими партнерами для достижения общих целей.',
    excellence: 'Превосходство',
    excellence_description: 'Мы стремимся к превосходству во всем, что делаем, от проектирования и производства продуктов до обслуживания клиентов и поддержки.',
    sustainability: 'Устойчивость',
    sustainability_description: 'Мы привержены экологически ответственным практикам, разрабатывая решения, которые сокращают выбросы и способствуют более чистой энергии.',
    our_team: 'Наша команда',
    team_description: 'Познакомьтесь с командой, которая движет наше видение вперед.',
    ceo_name: 'Алишер Каримов',
    ceo_title: 'Генеральный директор',
    ceo_description: 'С более чем 20-летним опытом в автомобильной промышленности, Алишер руководит нашей компанией с видением и экспертизой.',
    cto_name: 'Дилшод Рахимов',
    cto_title: 'Технический директор',
    cto_description: 'Дилшод курирует наши технические операции, обеспечивая, что мы остаемся на переднем крае технологии автомобильного газа.',
    coo_name: 'Нодира Азимова',
    coo_title: 'Операционный директор',
    coo_description: 'Нодира обеспечивает бесперебойную работу наших ежедневных операций, координируя наши 12 филиалов по всему Узбекистану.',
    join_our_journey: 'Присоединяйтесь к нашему пути',
    join_journey_description: 'Независимо от того, являетесь ли вы клиентом, партнером или потенциальным членом команды, мы хотели бы связаться с вами.',
    view_openings: 'Посмотреть вакансии',

    // Branches Page
    our_branches: 'Наши филиалы',
    find_branches_description: 'Найдите филиалы и сервисные центры EuropeGAS по всему Узбекистану',
    search_branches_placeholder: 'Поиск филиалов по названию, городу или адресу...',
    clear: 'Очистить',
    region: 'Регион',
    all_regions: 'Все регионы',
    locations: 'Местоположения',
    branch: 'Филиал',
    branches_label: 'Филиалы',
    no_branches_found: 'Филиалы, соответствующие вашим критериям, не найдены',
    clear_filters: 'Очистить фильтры',
    interactive_map: 'Интерактивная карта',
    no_branches_for_map: 'Нет филиалов для отображения на карте',
    loading_map: 'Загрузка карты...',
    city: 'Город',
    phone: 'Телефон',
    working_hours: 'Рабочие часы',
    weekdays: 'Будни',
    saturday: 'Суббота',
    sunday: 'Воскресенье',
    address: 'Адрес',
    email: 'Электронная почта',
    
    // Contact Page
    'get_in_touch': 'Связаться с нами',
    'send_us_message': 'Отправить нам сообщение',
    'your_name': 'Ваше имя',
    'your_email': 'Ваш email',
    'subject': 'Тема',
    'your_message': 'Ваше сообщение',
    'send_message': 'Отправить сообщение',
    'sending': 'Отправка...',
    'message_sent': 'Сообщение успешно отправлено!',
    'find_us_on_map': 'Найти нас на карте',
    
    // Cart Page
    'shopping_cart': 'Корзина покупок',
    'review_selected_products': 'Просмотрите выбранные продукты EuropeGAS',
    'product': 'Продукт',
    'price': 'Цена',
    'quantity': 'Количество',
    'total': 'Итого',
    'remove': 'Удалить',
    'cart_empty': 'Ваша корзина пуста',
    'start_shopping': 'Начните покупки, чтобы добавить товары в корзину',
    'order_summary': 'Сводка заказа',
    'subtotal': 'Промежуточный итог',
    'shipping': 'Доставка',
    'free': 'Бесплатно',
    'total_amount': 'Общая сумма',
    'checkout': 'Оформить заказ',
    
    // Product Detail Page
    'product_details': 'Детали продукта',
    'add_to_cart': 'Добавить в корзину',
    'in_stock': 'В наличии',
    'out_of_stock': 'Нет в наличии',
    'features': 'Особенности',
    'specifications': 'Характеристики',
    'related_products': 'Похожие продукты',
    'customer_reviews': 'Отзывы клиентов',
    'rating': 'Рейтинг',
    'reviews': 'Отзывы',
    'back_to_products': 'Назад к продуктам',
    'availability': 'Наличие',
    'key_features': 'Основные характеристики',
    
    // Common UI
    'loading': 'Загрузка...',
    'error': 'Ошибка',
    'try_again': 'Попробовать снова',
    'close': 'Закрыть',
    'open': 'Открыть',
    'save': 'Сохранить',
    'cancel': 'Отмена',
    'confirm': 'Подтвердить',
    'yes': 'Да',
    'no': 'Нет',
    'next': 'Далее',
    'previous': 'Назад',
    'view_all': 'Посмотреть все',
    'show_more': 'Показать больше',
    'show_less': 'Показать меньше',
    
    // HomePage specific
    'featured_products': 'Рекомендуемые продукты',
    'customer_testimonials': 'Что говорят наши клиенты',
    'find_us_nearby': 'Найти нас рядом',
    'view_details': 'Подробности',
    'product_categories': 'Категории продуктов',
    'featured_products_description': 'Откройте для себя наше самое популярное автомобильное газовое оборудование, разработанное с точностью и созданное на века.',
    'view_all_products': 'Посмотреть все продукты',
    'customer_testimonials_description': 'Не верьте нам на слово. Послушайте наших довольных клиентов по всему Узбекистану.',
    'find_us_nearby_description': '150 филиалов по всему Узбекистану - мы всегда рядом. Посетите нас сегодня.',
    'found': 'найдено',
    'show_all_cities': 'Показать все города',
    'ready_to_upgrade': 'Готовы модернизировать свой автомобиль?',
    'upgrade_description': 'Почувствуйте разницу с премиальным автомобильным газовым оборудованием EuropeGAS.',
    'contact_us_today': 'Свяжитесь с нами сегодня',
    
    // Missing translation keys
    'company_description': 'Узбекистан',
    'empowering_mission': 'Расширение возможностей автомобильного будущего Узбекистана с помощью инновационных газовых решений и бескомпромиссного качества',
    'contact_description': 'Мы будем рады услышать от вас. Свяжитесь с нашей командой.',
    'contact_form_description': 'Есть вопрос или хотите сотрудничать? Мы будем рады услышать от вас. Отправьте нам сообщение, и мы ответим как можно скорее.',
    'enter_full_name': 'Введите ваше полное имя',
    'enter_email_address': 'Введите ваш адрес электронной почты',
    'message_subject_placeholder': 'О чем это сообщение?',
    'message_placeholder': 'Расскажите больше о вашем запросе...',
    'monday_friday': 'Понедельник - Пятница:',
    'sunday_closed': 'Воскресенье: Закрыто',
    'headquarters_description': 'Посетите наш головной офис в центре Ташкента. Мы легко доступны и всегда готовы вас приветствовать.',
    'our_location': 'Наше местоположение',
    'phone_numbers': 'Телефоны',
    'email_address': 'Электронная почта',
    'privacy_policy': 'Политика конфиденциальности',
    'terms_of_service': 'Условия использования',
  },
  uz: {
    // Header
    'home': 'Bosh sahifa',
    'products': 'Mahsulotlar',
    'services': 'Xizmatlar',
    'branches': 'Filiallar',
    'about_us': 'Biz haqimizda',
    'contact': 'Aloqa',
    'search': 'Qidirish',
    'cart': 'Savat',
    'login': 'Kirish',
    'profile': 'Profil',
    
    // Buttons
    'explore_our_services': 'Xizmatlarimiz bilan tanishing',
    'contact_us': 'Biz bilan bog\'laning',
    'continue_shopping': 'Xaridni davom ettirish',
    'clear_cart': 'Savatni tozalash',
    'proceed_to_checkout': 'Buyurtma berish',
    
    // Product-related
    'ecu_control_units': 'ECU boshqaruv bloklari',
    'rail_injectors': 'Rels injektorlari',
    'gas_reducers': 'Gaz reductorlari',
    'all_products': 'Barcha mahsulotlar',
    'product_page_description': 'Optimal ishlash va ishonchlilik uchun mo\'ljallangan yuqori sifatli {category} tanlovimizni ko\'rib chiqing.',
    'search_products_placeholder': 'Mahsulotlarni qidirish...',
    'sort_by': 'Saralash turi',
    'price_low_to_high': 'Narxi: pastdan yuqoriga',
    'price_high_to_low': 'Narxi: yuqoridan pastga',
    'name_a_to_z': 'Nomi: A dan Z gacha',
    'name_z_to_a': 'Nomi: Z dan A gacha',
    'filters': 'Filtrlar',
    'price_range': 'Narx oralig\'i',
    'min_price': 'Minimal narx',
    'max_price': 'Maksimal narx',
    'no_products_found': 'Mahsulotlar topilmadi',
    'try_different_search': 'Qidiruv yoki filtr parametrlarini o\'zgartiring',
    'updating_products': 'Mahsulotlar yangilanmoqda...',
    
    // Service page
    'our_services': 'Bizning xizmatlar',
    'service_description': 'EuropeGAS gaz uskunalari uchun o\'rnatishdan tortib texnik xizmat ko\'rsatish va optimallashtirishgacha bo\'lgan keng qamrovli echimlarni taqdim etadi.',
    'core_services': 'Asosiy xizmatlar',
    'core_services_description': 'Biz gaz uskunalari va temir yo\'l tizimlaringiz ehtiyojlarini qondirish uchun maxsus xizmatlarning keng doirasini taqdim etamiz.',
    'equipment_installation': 'Uskunalarni o\'rnatish',
    'equipment_installation_desc': 'Gaz uskunalari va temir yo\'l tizimlarini aniqlik va e\'tibor bilan professional o\'rnatish.',
    'maintenance_repair': 'Texnik xizmat ko\'rsatish va ta\'mirlash',
    'maintenance_repair_desc': 'Tizimlaringizni optimal ishlashi uchun keng qamrovli texnik xizmat ko\'rsatish va ta\'mirlash xizmatlari.',
    'consultation_services': 'Maslahat xizmatlari',
    'consultation_services_desc': 'Gaz va temir yo\'l tizimi ehtiyojlaringiz uchun expert maslahat va rejalashtirish.',
    'system_optimization': 'Tizimni optimallashtirish',
    'system_optimization_desc': 'Mavjud tizimlaringizning ishlashini va samaradorligini oshirish.',
    'additional_services': 'Qo\'shimcha xizmatlar',
    'performance_testing': 'Ishlash sifatini tekshirish',
    'performance_testing_desc': 'Tizimlaringiz eng yuqori ishlash standartlariga javob berishini ta\'minlash uchun keng qamrovli testlash.',
    'delivery_logistics': 'Yetkazib berish va logistika',
    'delivery_logistics_desc': 'O\'zbekiston bo\'ylab barcha uskunalar va qismlar uchun ishonchli yetkazib berish xizmatlari.',
    'equipment_testing': 'Uskunalarni sinash',
    'equipment_testing_desc': 'Barcha uskunalar xavfsizlik va ishlash standartlariga javob berishini ta\'minlash uchun to\'liq testlash xizmatlari.',
    'training_programs': 'O\'quv dasturlari',
    'training_programs_desc': 'Operatorlar va texnik xizmat ko\'rsatuvchi xodimlar uchun uskunalardan to\'g\'ri foydalanish va parvarish qilish bo\'yicha keng qamrovli treninglar.',
    'our_service_process': 'Bizning xizmat ko\'rsatish jarayonimiz',
    'consultation': 'Maslahat',
    'consultation_desc': 'Biz sizning ehtiyojlaringiz va talablaringizni batafsil muhokama qilamiz',
    'assessment': 'Baholash',
    'assessment_desc': 'Bizning mutaxassislarimiz sizning aniq vaziyatingizni baholaydilar',
    'implementation': 'Amalga oshirish',
    'implementation_desc': 'Biz kerakli xizmat yoki o\'rnatishni amalga oshiramiz',
    'follow_up': 'Kuzatib borish',
    'follow_up_desc': 'Biz sizning to\'liq qoniqishingizni ta\'minlaymiz',
    'ready_to_get_started': 'Boshlashga tayyormisiz?',
    'cta_description': 'Bugun biz bilan bog\'laning va gaz uskunalari hamda temir yo\'l yechimlari bo\'yicha qanday yordam bera olishimiz haqida gaplashamiz.',
    
    // Service details
    'gas_conversion_systems': 'Gaz konversiya tizimlari',
    'fuel_management_systems': 'Yoqilg\'i boshqaruv tizimlari',
    'rail_equipment_setup': 'Temir yo\'l uskunalarini sozlash',
    'preventive_maintenance': 'Oldini oluvchi texnik xizmat',
    'emergency_repairs': 'Favqulodda ta\'mirlash',
    'system_diagnostics': 'Tizim diagnostikasi',
    'system_design': 'Tizim dizayni',
    'efficiency_analysis': 'Samaradorlik tahlili',
    'regulatory_compliance': 'Qonuniy muvofiqlik',
    'performance_tuning': 'Ishlash sifatini sozlash',
    'efficiency_upgrades': 'Samaradorlikni oshirish',
    'system_integration': 'Tizim integratsiyasi',
    
    // HomePage
    'home_hero_alt': 'Avtomobil gaz jihozlari',
    'home_hero_title': 'Premium avtomobil gaz jihozlari',
    'home_hero_subtitle': 'Mukammallik uchun ishlab chiqilgan. Yuqori samaradorlik uchun mo\'ljallangan.',
    'explore_products': 'Mahsulotlarni ko\'rish',
    'learn_more': 'Batafsil ma\'lumot',
    'innovation_section_title': 'Eng yaxshi innovatsiyalar',
    'innovation_section_description': '15 yildan ortiq tajriba bilan, biz avtomobil gaz jihozlari sohasida mukammallikka erishdik, tengsiz sifat va samaradorlikni taqdim etamiz.',
    'feature_1_title': 'Sifat kafolati',
    'feature_1_description': 'Barcha mahsulotlarimiz xotirjamligingiz uchun kafolat va sifat kafolati bilan keladi.',
    'feature_2_title': 'Professional o\'rnatish',
    'feature_2_description': 'Bizning sertifikatlangan texniklar optimal ishlash uchun to\'g\'ri o\'rnatishni ta\'minlaydilar.',
    'feature_3_title': '12 ta mintaqaviy filiallar',
    'feature_3_description': 'O\'zbekiston bo\'ylab filiallarimiz bilan biz har doim mijozlarimizga yaqinmiz.',
    'feature_4_title': '100,000+ mijozlar',
    'feature_4_description': 'Mahsulot va xizmatlarimizga ishongan mamnun mijozlarimiz oilasiga qo\'shiling.',
    
    // Footer
    'company': 'Kompaniya',
    'quick_links': 'Tezkor havolalar',
    'newsletter': 'Yangiliklar',
    'copyright': 'Mualliflik huquqi © 2024 EuropeGAS. Barcha huquqlar himoyalangan.',
    
    // Language
    'language': 'Til',
    'english': 'Ingliz',
    'uzbek': 'O\'zbek',

    // About Page
    about_page_title: 'Biz haqimizda',
    about_hero_alt: 'EuropeGAS bosh ofisi',
    about_hero_description: 'EuropeGAS — 2005 yildan beri avtomobil gaz yechimlari bo\'yicha yetakchi.',
    our_mission: 'Bizning vazifamiz',
    mission_description: 'Biz mijozlarning o\'zgaruvchan ehtiyojlarini qondiradigan barqaror va samarali energiya yechimlarini taqdim etamiz, bunda sifat, xavfsizlik va ekologik mas\'uliyatning eng yuqori standartlarini saqlaymiz.',
    innovation: 'Innovatsiya',
    innovation_description: 'Biz avtomobil gaz texnologiyasida mumkin bo\'lgan chegaralarni doimo kengaytirib, yanada samarali, ishonchli va foydalanuvchiga qulay yechimlarni ishlab chiqamiz.',
    customer_focus: 'Mijozlarga e\'tibor',
    customer_focus_description: 'Biz ishlab chiqadigan har bir mahsulot va ko\'rsatadigan xizmatimiz mijozlarimiz ehtiyojlariga qaratilgan, bu ularga eng yaxshi tajriba va qiymatni olishlarini ta\'minlaydi.',
    our_journey: 'Bizning yo\'limiz',
    journey_description: 'Kamtarona boshlanishdan Markaziy Osiyoda avtomobil gaz yechimlari bo\'yicha yetakchiga aylanishgacha.',
    foundation: 'Asos',
    foundation_description: 'EuropeGAS yuqori sifatli avtomobil gaz yechimlarini taqdim etish maqsadida O\'zbekiston ning Toshkent shahrida tashkil etilgan.',
    expansion: 'Kengaytirish',
    expansion_description: 'Biz O\'zbekiston ning bir nechta mintaqalarida faoliyatimizni kengaytirdik va Yevropa ishlab chiqaruvchilari bilan hamkorlik o\'rnatdik.',
    today: 'Bugun',
    today_description: 'Hozirda mamlakatimizda 12 ta filial va 100 000 dan ortiq mamnun mijozlar bilan biz innovatsion yechimlar bilan bozorda yetakchilik qilishni davom ettiramiz.',
    our_values: 'Bizning qadriyatlarimiz',
    values_description: 'Barcha ishlarimizni boshqaradigan tamoyillar.',
    integrity: 'Halollik',
    integrity_description: 'Biz o\'z biznesimizni eng yuqori axloqiy standartlar bilan olib boramiz, barcha munosabatlarimizda shaffoflik va ishonchni ta\'minlaymiz.',
    teamwork: 'Jamoaviy ish',
    teamwork_description: 'Biz hamkorlikning kuchiga ishonamiz, bo\'limlar va hamkorlarimiz bilan birgalikda umumiy maqsadlarga erishish uchun ishlash.',
    excellence: 'Mukammallik',
    excellence_description: 'Biz qiladigan har bir ishda mukammallikka intilamiz, mahsulot dizaynidan tortib ishlab chiqarish, mijozlarga xizmat ko\'rsatish va qo\'llab-quvvatlashgacha.',
    sustainability: 'Barqarorlik',
    sustainability_description: 'Biz ekologik mas\'uliyatli amaliyotga sodiqmiz, chiqindilarni kamaytiradigan va toza energiyani rivojlantiradigan yechimlarni ishlab chiqamiz.',
    our_team: 'Bizning jamoa',
    team_description: 'Bizning ko\'rish qarashimizni oldinga siljitadigan jamoa bilan tanishing.',
    ceo_name: 'Alisher Karimov',
    ceo_title: 'Bosh ijrochi direktor',
    ceo_description: 'Avtomobil sanoatida 20 yildan ortiq tajribaga ega bo\'lgan Alisher kompaniyamizni ko\'rish qarash va tajriba bilan boshqaradi.',
    cto_name: 'Dilshod Rahimov',
    cto_title: 'Bosh texnologiya bo\'yicha direktor',
    cto_description: 'Dilshod texnik operatsiyalarimizni nazorat qiladi, biz avtomobil gaz texnologiyasi sohasida eng yuqori darajada qolishimizni ta\'minlaydi.',
    coo_name: 'Nodira Azimova',
    coo_title: 'Bosh operatsion direktor',
    coo_description: 'Nodira kundalik operatsiyalarimiz ravon ishlashini ta\'minlaydi, O\'zbekiston bo\'ylab 12 ta filialimizni muvofiqlashtiradi.',
    join_our_journey: 'Bizning yo\'limizga qo\'shiling',
    join_journey_description: 'Siz mijoz, hamkor yoki potensial jamoa a\'zosi bo\'lishingizdan qat\'i nazar, biz siz bilan bog\'lanishni xohlaymiz.',
    view_openings: 'Bo\'sh ish o\'rinlarini ko\'rish',

    // Branches Page
    our_branches: 'Bizning filiallarimiz',
    find_branches_description: 'O\'zbekiston bo\'ylab EuropeGAS filiallari va xizmat markazlarini toping',
    search_branches_placeholder: 'Filiallarni nomi, shahri yoki manzili bo\'yicha qidiring...',
    clear: 'Tozalash',
    region: 'Viloyat',
    all_regions: 'Barcha viloyatlar',
    locations: 'Manzillar',
    branch: 'Filial',
    branches_label: 'Filiallar',
    no_branches_found: 'Sizning mezonlaringizga mos keladigan filiallar topilmadi',
    clear_filters: 'Filtrlarni tozalash',
    interactive_map: 'Interaktiv xarita',
    no_branches_for_map: 'Xaritada ko\'rsatish uchun filiallar yo\'q',
    loading_map: 'Xarita yuklanmoqda...',
    city: 'Shahar',
    phone: 'Telefon',
    working_hours: 'Ish vaqti',
    weekdays: 'Ish kunlari',
    saturday: 'Shanba',
    sunday: 'Yakshanba',
    address: 'Manzil',
    email: 'Elektron pochta',
    
    // Contact Page
    'get_in_touch': 'Biz bilan bog\'laning',
    'send_us_message': 'Bizga xabar yuboring',
    'your_name': 'Sizning ismingiz',
    'your_email': 'Sizning emailingiz',
    'subject': 'Mavzu',
    'your_message': 'Sizning xabaringiz',
    'send_message': 'Xabar yuborish',
    'sending': 'Yuborilmoqda...',
    'message_sent': 'Xabar muvaffaqiyatli yuborildi!',
    'find_us_on_map': 'Bizni xaritada toping',
    
    // Cart Page
    'shopping_cart': 'Xarid savati',
    'review_selected_products': 'Tanlangan EuropeGAS mahsulotlarini ko\'rib chiqing',
    'product': 'Mahsulot',
    'price': 'Narx',
    'quantity': 'Miqdor',
    'total': 'Jami',
    'remove': 'O\'chirish',
    'cart_empty': 'Savatingiz bo\'sh',
    'start_shopping': 'Savatga mahsulot qo\'shish uchun xarid qilishni boshlang',
    'order_summary': 'Buyurtma xulosasi',
    'subtotal': 'Oraliq jami',
    'shipping': 'Yetkazib berish',
    'free': 'Bepul',
    'total_amount': 'Umumiy summa',
    'checkout': 'Buyurtma berish',
    
    // Product Detail Page
    'product_details': 'Mahsulot tafsilotlari',
    'add_to_cart': 'Savatga qo\'shish',
    'in_stock': 'Mavjud',
    'out_of_stock': 'Mavjud emas',
    'features': 'Xususiyatlar',
    'specifications': 'Texnik xususiyatlar',
    'related_products': 'O\'xshash mahsulotlar',
    'customer_reviews': 'Mijozlar sharhlari',
    'rating': 'Reyting',
    'reviews': 'Sharhlar',
    'back_to_products': 'Mahsulotlarga qaytish',
    'availability': 'Mavjudlik',
    'key_features': 'Asosiy xususiyatlar',
    
    // Common UI
    'loading': 'Yuklanmoqda...',
    'error': 'Xatolik',
    'try_again': 'Qayta urinib ko\'ring',
    'close': 'Yopish',
    'open': 'Ochish',
    'save': 'Saqlash',
    'cancel': 'Bekor qilish',
    'confirm': 'Tasdiqlash',
    'yes': 'Ha',
    'no': 'Yo\'q',
    'next': 'Keyingi',
    'previous': 'Oldingi',
    'view_all': 'Barchasini ko\'rish',
    'show_more': 'Ko\'proq ko\'rsatish',
    'show_less': 'Kamroq ko\'rsatish',
    
    // HomePage specific
    'featured_products': 'Tavsiya etilgan mahsulotlar',
    'customer_testimonials': 'Mijozlarimiz nima deyishadi',
    'find_us_nearby': 'Bizni yaqin atrofda toping',
    'view_details': 'Tafsilotlar',
    'product_categories': 'Mahsulot toifalari',
    'featured_products_description': 'Aniqlik bilan ishlab chiqilgan va uzoq muddat davomida xizmat qilish uchun yaratilgan eng mashhur avtomobil gaz jihozlarimizni kashf eting.',
    'view_all_products': 'Barcha mahsulotlarni ko\'rish',
    'customer_testimonials_description': 'Bizning so\'zimizga ishonmang. O\'zbekiston bo\'ylab mamnun mijozlarimizdan eshiting.',
    'find_us_nearby_description': 'O\'zbekiston bo\'ylab 150 ta filial - biz hech qachon uzoqda emasmiz. Bugun bizga tashrif buyuring.',
    'found': 'topildi',
    'show_all_cities': 'Barcha shaharlarni ko\'rsatish',
    'ready_to_upgrade': 'Avtomobilingizni yangilashga tayyormisiz?',
    'upgrade_description': 'EuropeGAS ning premium avtomobil gaz jihozlari bilan farqni his eting.',
    'contact_us_today': 'Bugun biz bilan bog\'laning',
    
    // Missing translation keys
    'company_description': 'O\'zbekiston',
    'empowering_mission': 'O\'zbekiston ning avtomobil kelajagini innovatsion gaz yechimlari va murosasiz sifat bilan kuchaytirish',
    'contact_description': 'Sizdan eshitishdan xursandmiz. Bizning jamoa bilan bog\'laning.',
    'contact_form_description': 'Savolingiz bormi yoki hamkorlik qilishni xohlaysizmi? Sizdan eshitishdan xursandmiz. Bizga xabar yuboring va biz imkon qadar tezroq javob beramiz.',
    'enter_full_name': 'To\'liq ismingizni kiriting',
    'enter_email_address': 'Elektron pochta manzilingizni kiriting',
    'message_subject_placeholder': 'Bu xabar nima haqida?',
    'message_placeholder': 'So\'rovingiz haqida batafsil ma\'lumot bering...',
    'monday_friday': 'Dushanba - Juma:',
    'sunday_closed': 'Yakshanba: Yopiq',
    'headquarters_description': 'Toshkent markazidagi bosh ofisimizga tashrif buyuring. Biz oson kirish mumkin va sizni har doim kutib olamiz.',
    'our_location': 'Bizning manzil',
    'phone_numbers': 'Telefon raqamlar',
    'email_address': 'Elektron pochta',
    'privacy_policy': 'Maxfiylik siyosati',
    'terms_of_service': 'Foydalanish shartlari',
    
    // Additional translations for Uzbek
    'russian': 'Rus',
  }
};

// Helper function to get translation and replace parameters
const getTranslation = (lang: Language, key: string, params?: Record<string, string>): string => {
  let translation = translations[lang][key] || key;
  
  // Replace parameters if provided
  if (params) {
    Object.entries(params).forEach(([param, value]) => {
      translation = translation.replace(`{${param}}`, value);
    });
  }
  
  return translation;
};

// Create the provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Get saved language preference or default to Russian
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    return savedLanguage || 'ru';
  });

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem('language', language);
    // Set html lang attribute for accessibility and SEO
    document.documentElement.lang = language;
  }, [language]);

  // Translation function
  const t = (key: string, params?: Record<string, string>): string => {
    return getTranslation(language, key, params);
  };

  const value = {
    language,
    setLanguage,
    t,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

// Custom hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext; 